import ky from 'ky'
import io from 'socket.io-client'
import DataLoader from 'dataloader'

import { DeepPartial } from '../hooks/use-synchronize-data'

import { Store } from '../state'
import {
  SpaceCocoons,
  SpaceInfo,
  SpaceList,
  SpaceMaintenance,
  SpaceOptions,
  //4261 ajouter les horaires aux salles
  RoomOptions,
} from '../state/models/space'
import {
  AudioBackground,
  AudioBook,
  AudioTheme,
  Gong,
} from '../state/models/audio'
import { User } from '../state/models/users'
import { createCallCache } from '../helpers/cache-calls'
import { isExpired } from './session'

type Update<T> = DeepPartial<T> & { _id: string }

const socket = io(process.env.REACT_APP_API_URL as string, {
  path: '/harmonyjs-socket',
})

const spaceInfoSelection = `
_id, localizedName, localizedDescription, code
codes {
  code, expiration
}
emails {
  email
}
subscription {
  start, end, premium, licenses
  disabled {
    audio, profile, booking
  }
}`

const spaceOptionsSelection = `
_id
options {
  napDelay
  napsPerDay
  napDurations { duration10, duration15, duration20, duration25,duration45,duration60 }
  napDays { 
    monday { startHour, endHour }
    tuesday { startHour, endHour }
    wednesday { startHour, endHour }
    thursday { startHour, endHour }
    friday { startHour, endHour }
    saturday { startHour, endHour }
    sunday { startHour, endHour }
  }
}`
//4261 Ajouter les horaires aux salles
const roomOptionsSelection = `
_id
options {
  napDays { 
    monday { startHour, endHour }
    tuesday { startHour, endHour }
    wednesday { startHour, endHour }
    thursday { startHour, endHour }
    friday { startHour, endHour }
    saturday { startHour, endHour }
    sunday { startHour, endHour }
  }
}`

const spaceMaintenanceSelection = `
_id
maintenances { target, start, end }
cocoons { _id, name, room { _id } }
rooms { _id, name }`

const audioThemeSelection = `
_id, slug, title, short, description, tracks, image, cover, contentCategory { _id, short, long }`
const audioBookSelection = `
_id, slug, language, title { short, long }, description, backgrounds
image, cover
chapters { title, tracks }, contentCategory { _id, short, long }`
const audioBackgroundSelection = `
_id, slug, short, long, tracks, image, contentCategory { _id, short, long }`
const gongSelection = `
_id, slug, short, image, cover`
const categorySelection = `
_id, slug, short, long, image, type, parent { _id, short, long}`

function serializeValue(value: any): string {
  if ((value ?? null) === null) {
    return 'null'
  }

  if (Array.isArray(value)) {
    return `[${value.map(serializeValue)}]`
  }

  if (typeof value === 'object') {
    return serializeRecord(value)
  }

  return JSON.stringify(value)
}

function serializeRecord(record: object): string {
  type Key = keyof typeof record
  return `{
    ${Object.keys(record).map(
      (key) => `${key}: ${serializeValue(record[key as Key])}\n`,
    )}
  }`
}

const callCache = createCallCache(500)

export const graphql = {
  subscribe(models: string[], callback: () => void | Promise<void>) {
    callback()

    models.forEach((m) => {
      socket.on(m + '-updated', callback)
    })

    return () => {
      models.forEach((m) => {
        socket.off(m + '-updated', callback)
      })
    }
  },
  async query<T = {}>(query: string) {
    const token = Store.getState().utils.session.token

    // Le validateur JWT de l'API est non bloquant : un jeton expiré ne produit
    // pas un 401 mais des erreurs GraphQL « Cannot … » incompréhensibles. On
    // coupe court et on renvoie l'utilisateur sur l'écran de connexion.
    // (Un jeton sans `exp` — rendu PDF par cron — n'est pas concerné.)
    if (token && isExpired(token)) {
      Store.getActions().utils.session.disconnect()
      throw new Error('session_expired')
    }

    const headers: Record<string, string> = {}

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const results = await ky
      .post(process.env.REACT_APP_API_URL as string, {
        headers,
        json: {
          operationName: null,
          variables: {},
          query,
        },
      })
      .json<{ data: T; errors?: [Error] }>()

    if (results.errors) {
      throw results.errors[0]
    }

    return results.data
  },
  queries: {
    // `deviceToken` : appareil de confiance (#7512). Présent ⇒ l'API peut
    // ouvrir la session sans redemander de code.
    login(email: string, password: string, deviceToken?: string | null) {
      return graphql.query<{
        administratorLogin: {
          error: 'user_not_found' | 'wrong_credentials' | null
          token: string | null
          // `totp_required` : la 2FA est configurée, un code est attendu.
          // `totp_setup_required` : compte admin sans 2FA, il faut l'enrôler.
          status: 'ok' | 'totp_required' | 'totp_setup_required' | null
          // Jeton de défi : n'ouvre aucun accès, il ne sert qu'à enchaîner sur
          // l'enrôlement ou la vérification du code.
          challenge: string | null
        }
      }>(`{
        administratorLogin(login: "${email}", password: "${password}"${
        deviceToken ? `, deviceToken: "${deviceToken}"` : ''
      }) {
          error
          token
          status
          challenge
        }
      }`)
    },
    // Comptes administrateur et état de leur second facteur (écran
    // « Administrateurs »).
    getAdminAccounts() {
      return graphql.query<{
        administratorAdminAccounts: {
          _id: string
          email: string
          active: boolean
          // Compte créé mais dont l'invitation n'a pas encore été suivie.
          pending: boolean
          totpEnabled: boolean
          totpVerifiedAt: string | null
          trustedDevices: number
        }[]
      }>('{ administratorAdminAccounts }')
    },
    resetPassword(email: string) {
      return graphql.query<{
        administratorResetPassword: { status: string; error: string | null }
      }>(`{
        administratorResetPassword(email: "${email}") {
          status
          error
        }
      }`)
    },
    sendRecap(spaceId: string, month: number, year: number, annual: boolean) {
      return graphql.query<{
        spaceId: string
        month: number
        year: number
        annual: boolean
      }>(`{
        administratorSendRecap(spaceId: "${spaceId}", month: ${month}, year: ${year}, annual: ${annual})
      }`)
    },
    getAdministratorInfo() {
      return graphql.query<{
        administrator: {
          active: boolean
          email: string
          role: string | null
          totp: { enabled: boolean | null; verifiedAt: string | null } | null
        }
      }>(`{
        administrator {
          active
          email
          role
          totp {
            enabled
            verifiedAt
          }
        }
      }`)
    },
    getSpaces() {
      return graphql.query<{
        spaceList: Omit<SpaceList, 'status'>[]
      }>(
        `{
          spaceList {
            _id, name, code,
            localizedName,
            
            subscription { 
              start, end, premium, licenses,
            }
          }
        }`,
      )
    },
    getSpaceStatistics(id: string) {
      return spaceStatisticsLoader.load(id)
    },
    getSpacesStatistics(ids: readonly string[]) {
      return graphql.query<{
        spaceList: {
          _id: string
          statistics: {
            users: number
            naps: number
            rooms: number
            cocoons: number
          }
        }[]
      }>(
        `{
          spaceList(filter: { _operators: { _id: { in: ${JSON.stringify(
            ids,
          )} } } }) {
            _id
            statistics {
              users, naps, rooms, cocoons
            }
          }
        }`,
      )
    },
    async getSpaceInfo(id: string) {
      return (
        await graphql.query<{ space: SpaceInfo }>(`{
        space(filter: { _id: "${id}" }) { ${spaceInfoSelection} }
      }`)
      ).space
    },
    async getSpaceOptions(id: string) {
      return (
        await graphql.query<{ space: SpaceOptions }>(`{
        space(filter: { _id: "${id}" }) { ${spaceOptionsSelection} }
      }`)
      ).space
    },
    //4261 recuperer les options d'une salle
    async getRoomOptions(id: string) {
      return (
        await graphql.query<{ room: RoomOptions }>(`{
        room(filter: { _id: "${id}" }) { ${roomOptionsSelection} }
      }`)
      ).room
    },
    async getSpaceCocoons(id: string) {
      return (
        await graphql.query<{ space: SpaceCocoons }>(`{
        space(filter: { _id: "${id}" }) {
          _id
          localizedName
          rooms { _id, name, localizedName, localizedDescription }
          cocoons { 
            _id, localizedName, localizedDescription, version
            room { _id }    
          }
        }
      }`)
      ).space
    },
    async getSpaceMaintenance(id: string) {
      return (
        await graphql.query<{ space: SpaceMaintenance }>(`{
        space(filter: { _id: "${id}" }) { ${spaceMaintenanceSelection} }
      }`)
      ).space
    },
    getAudioThemes() {
      return graphql.query<{
        audioThemeList: AudioTheme[]
      }>(`{ audioThemeList { ${audioThemeSelection} } }`)
    },
    async getAudioTheme(id: string, force = false) {
      return (
        await callCache(graphql.query, { key: 'getAudioTheme:' + id, force })<{
          audioThemeList: AudioTheme[]
        }>(
          `{  audioThemeList(filter: { slug: "${id}" }) { ${audioThemeSelection} } }`,
        )
      ).audioThemeList[0]
    },
    getAudioBooks() {
      return graphql.query<{
        audioBookList: AudioBook[]
      }>(`{ audioBookList { ${audioBookSelection} } }`)
    },
    async getAudioBook(id: string, force = false) {
      return (
        await callCache(graphql.query, { key: 'getAudioBook:' + id, force })<{
          audioBookList: AudioBook[]
        }>(
          `{ audioBookList(filter: { slug: "${id}" }) { ${audioBookSelection} } }`,
        )
      ).audioBookList[0]
    },
    getAudioBackgrounds() {
      return graphql.query<{
        audioBackgroundList: AudioBackground[]
      }>(`{ audioBackgroundList { ${audioBackgroundSelection} } }`)
    },
    getGongs() {
      return graphql.query<{
        gongList: Gong[]
      }>(`{ gongList { ${gongSelection} } }`)
    },
    async getGong(id: string, force = false) {
      return (
        await callCache(graphql.query, { key: 'getGong:' + id, force })<{
          gongList: Gong[]
        }>(`{ gongList(filter: { slug: "${id}" }) { ${gongSelection} } }`)
      ).gongList[0]
    },
    getCategories() {
      return graphql.query<{
        categoryList: import('../state/models/audio').Category[]
      }>(`{ categoryList { ${categorySelection} } }`)
    },
    async getAudioBackground(id: string, force = false) {
      return (
        await callCache(graphql.query, {
          key: 'getAudioBackground:' + id,
          force,
        })<{
          audioBackgroundList: AudioBackground[]
        }>(
          `{ audioBackgroundList(filter: { slug: "${id}" }) { ${audioBackgroundSelection} } }`,
        )
      ).audioBackgroundList[0]
    },
    getUsers({
      search,
      space,
      skip,
      limit,
    }: {
      search?: string
      space?: string | null
      skip: number
      limit: number
    }) {
      const filterParts: string[] = []
      if (space) {
        filterParts.push(`spaces: "${space}"`)
      }
      const trimmed = (search ?? '').trim()
      if (trimmed) {
        const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        filterParts.push(`_operators: { email: { regex: "/${escaped}/i" } }`)
      }
      const filter = filterParts.length
        ? `filter: { ${filterParts.join(' ')} }`
        : ''

      return graphql.query<{
        userList: User[]
        userCount: number
      }>(`{
        userList(${filter} skip: ${skip} limit: ${limit} sort: { email: 1 }) {
          _id, email, spaces { _id, name }
        }
        userCount${filter ? `(${filter})` : ''}
      }`)
    },
    getNaps(space: string, from: Date, to: Date, withDetails: boolean = false) {
      return graphql.query<{
        napList: {
          _id: string
          user: string
          userInfo?: { email: string; _id: string }
          time: { start: string; end: string }
          cocoon: { _id: string; name: string; room: { _id: string } }
          space: string
          evaluation: { rating: number; comment: string }
        }[]
      }>(`{
        napList(
          filter: {
            ${space ? `space: "${space}"` : ''}
            _operators: { time: { match: { start: { gte: "${from.toISOString()}" }, end: { lte: "${to.toISOString()}" } } } }
          }
        ) {
          _id
          user
          ${withDetails ? 'userInfo { email }' : ''}
          time { start, end }
          cocoon { _id, name, room { _id } }
          evaluation { rating, comment }
          space
        }
      }`)
    },
    getUserNaps(user: string, past?: boolean) {
      return graphql.query<{
        napList: {
          _id: string
          time: { start: string; end: string }
          cocoon: { name: string; room: { name: string } }
        }[]
      }>(`{
        napList(
          filter: {
            user: "${user}"
            _operators: { time: { match: { start: { ${
              past ? 'lte' : 'gte'
            }: "${new Date().toISOString()}" } } } }
          }
        ) {
          _id
          time { start, end }
          cocoon { _id, name, room { _id, name } }
        }
      }`)
    },
    getUserSpace(space: string, to: Date, skip: number, limit: number) {
      return graphql.query<{
        userSpaceList: {
          user: string
          space: string
          join: string
          leave?: string
        }[]
      }>(`{
        userSpaceList(filter: {
          ${space ? `space: "${space}"` : ''}
          _operators: {
            join: {
              lte: "${to.toISOString()}"
            }
          }
        }, skip: ${skip}, limit: ${limit}) { user, join, leave, space }
      }`)
    },
    // Comptes d'accès "client" rattachés à un espace
    getClientAccounts(space: string) {
      return graphql.query<{
        administratorClientAccounts: {
          _id: string
          email: string
          active: boolean
          spaces: string[]
        }[]
      }>(`{
        administratorClientAccounts(space: "${space}")
      }`)
    },
  },
  mutations: {
    // Administrator
    async setupAdministrator(password: string) {
      await graphql.query(`mutation {
        administratorSetup(password: "${password}")
      }`)
    },
    setNewPassword(code: string, password: string) {
      return graphql.query<{ administratorSetNewPassword: boolean }>(
        `mutation { administratorSetNewPassword(code: "${code}", password: "${password}") }`,
      )
    },
    // Space
    async createSpace(space: {
      localizedName: { fr: string }
      codes: [{ code: string }]
    }) {
      return graphql.query<{ spaceCreate: { _id: string } }>(`mutation {
        spaceCreate(record: ${serializeRecord(space)}) { _id }
      }`)
    },
    async updateSpaceInfo(info: Partial<SpaceInfo> & { _id: string }) {
      const result = (
        await graphql.query<{ spaceUpdate: SpaceInfo }>(`mutation {
        spaceUpdate(record: ${serializeRecord(info)}) { ${spaceInfoSelection} }
      }`)
      ).spaceUpdate

      if (!result) {
        throw new Error('Error while updating Space')
      }

      return result
    },
    async updateSpaceOptions(options: Partial<SpaceOptions> & { _id: string }) {
      return (
        await graphql.query<{ spaceUpdate: SpaceOptions }>(`mutation {
        spaceUpdate(record: ${serializeRecord(
          options,
        )}) { ${spaceOptionsSelection} }
      }`)
      ).spaceUpdate
    },
    //4261 mettre a jour les options d'une salle
    async updateRoomOptions(options: Partial<RoomOptions> & { _id: string }) {
      return (
        await graphql.query<{ roomUpdate: RoomOptions }>(`mutation {
        roomUpdate(record: ${serializeRecord(
          options,
        )}) { ${roomOptionsSelection} }
      }`)
      ).roomUpdate
    },
    async updateSpaceMaintenance(
      maintenance: Partial<SpaceMaintenance> & { _id: string },
    ) {
      return (
        await graphql.query<{ spaceUpdate: SpaceMaintenance }>(`mutation {
        spaceUpdate(record: ${serializeRecord(
          maintenance,
        )}) { ${spaceMaintenanceSelection} }
      }`)
      ).spaceUpdate
    },
    async deleteSpace(space: string) {
      return graphql.query(`mutation {
        spaceDelete(_id: "${space}") { _id }
      }`)
    },

    // Cocoons & Rooms
    async addRoom(space: string) {
      return graphql.query(`mutation {
        roomCreate(record: { space: "${space}" }) { _id }
      }`)
    },
    async addCocoon(space: string, room: string) {
      return graphql.query(`mutation {
        cocoonCreate(record: { space: "${space}", room: "${room}" }) { _id }
      }`)
    },
    async updateRoom(room: Update<SpaceCocoons['rooms'][number]>) {
      return graphql.query(`mutation {
        roomUpdate(record: ${serializeRecord(room)}) { _id }
      }`)
    },
    async updateCocoon(cocoon: Update<SpaceCocoons['cocoons'][number]>) {
      return graphql.query(`mutation {
        cocoonUpdate(record: ${serializeRecord(cocoon)}) { _id }
      }`)
    },
    async deleteRoom(room: string) {
      return graphql.query(`mutation {
        roomDelete(_id: "${room}") { _id }
      }`)
    },
    async deleteCocoon(cocoon: string) {
      return graphql.query(`mutation {
        cocoonDelete(_id: "${cocoon}") { _id }
      }`)
    },

    // Audio themes
    async createAudioTheme(slug: string) {
      return graphql.query<{
        audioThemeCreate: { _id: string; slug: string }
      }>(`mutation {
        audioThemeCreate(record: { slug: "${slug}" }) { _id, slug }
      }`)
    },
    async updateAudioTheme(theme: Update<AudioTheme>) {
      return (
        await graphql.query<{ audioThemeUpdate: AudioTheme }>(`mutation {
        audioThemeUpdate(record: ${serializeRecord(
          theme,
        )}) { ${audioThemeSelection} }
      }`)
      ).audioThemeUpdate
    },
    async uploadAudioTheme(parameters: {
      duration: number
      part: number
      id: string
      language?: string
    }) {
      return graphql.query(`mutation { audioThemeTrackUpload(
        duration: ${parameters.duration},
        part: "${parameters.part}",
        themeId: "${parameters.id}",
        language: "${parameters.language ?? 'fr'}"
        cache: ${Date.now()}
      ) }`)
    },
    async uncacheAudioTheme(parameters: {
      duration: number
      part: number
      id: string
      language?: string
    }) {
      return graphql.query(`mutation { audioThemeTrackDelete(
        duration: ${parameters.duration},
        part: "${parameters.part}",
        themeId: "${parameters.id}",
        language: "${parameters.language ?? 'fr'}"
      ) }`)
    },
    async deleteAudioTheme(id: string) {
      return graphql.query<{
        audioThemeDelete: { _id: string }
      }>(`mutation {
        audioThemeDelete(_id: "${id}") { _id }
      }`)
    },

    // Audio books
    async createAudioBook(slug: string) {
      return graphql.query<{
        audioBookCreate: { _id: string; slug: string }
      }>(`mutation {
        audioBookCreate(record: { slug: "${slug}", language: "fr", chapters: [], backgrounds: [] }) { _id, slug }
      }`)
    },
    async updateAudioBook(book: Update<AudioBook>) {
      return (
        await graphql.query<{ audioBookUpdate: AudioBook }>(`mutation {
        audioBookUpdate(record: ${serializeRecord(
          book,
        )}) { ${audioBookSelection} }
      }`)
      ).audioBookUpdate
    },
    async setAudioBookChapterTitle(
      book: string,
      chapter: number,
      title: string,
    ) {
      return graphql.query(
        `mutation { audioBookSetChapterTitle(bookId: "${book}", chapterIndex: ${chapter}, title: "${title}") }`,
      )
    },
    async removeLastAudioBookChapter(book: string) {
      return graphql.query(
        `mutation { audioBookRemoveLastChapter(bookId: "${book}") }`,
      )
    },
    async uploadAudioBook(parameters: {
      id: string
      chapter?: number
      part: number
      duration: number
    }) {
      return graphql.query(`mutation { audioBookUpload(
        duration: ${parameters.duration},
        part: "${parameters.part}",
        bookId: "${parameters.id}",
        chapterIndex: ${parameters.chapter ?? 0}
        cache: ${Date.now()}
      ) }`)
    },
    async uncacheAudioBook(parameters: {
      id: string
      chapter?: number
      part: number
      duration: number
    }) {
      return graphql.query(`mutation { audioBookTrackDelete(
        duration: ${parameters.duration},
        part: "${parameters.part}",
        bookId: "${parameters.id}",
        chapterIndex: ${parameters.chapter ?? 0}
      ) }`)
    },
    async deleteAudioBook(id: string) {
      return graphql.query<{
        audioBookDelete: { _id: string }
      }>(`mutation {
        audioBookDelete(_id: "${id}") { _id }
      }`)
    },

    // Audio background
    async createAudioBackground(slug: string) {
      return graphql.query<{
        audioBackgroundCreate: { _id: string; slug: string }
      }>(`mutation {
        audioBackgroundCreate(record: { slug: "${slug}" }) { _id, slug }
      }`)
    },
    async updateAudioBackground(background: Update<AudioBackground>) {
      return (
        await graphql.query<{
          audioBackgroundUpdate: AudioBackground
        }>(`mutation {
        audioBackgroundUpdate(record: ${serializeRecord(
          background,
        )}) { ${audioBackgroundSelection} }
      }`)
      ).audioBackgroundUpdate
    },
    async uploadAudioBackground(parameters: { duration: number; id: string }) {
      return graphql.query(`mutation { audioBackgroundDurationTrackUpload(
        duration: ${parameters.duration},
        backgroundId: "${parameters.id}",
        cache: ${Date.now()}
      ) }`)
    },
    async uncacheAudioBackground(parameters: { duration: number; id: string }) {
      return graphql.query(`mutation { audioBackgroundDurationTrackDelete(
        duration: ${parameters.duration},
        backgroundId: "${parameters.id}",
      ) }`)
    },
    async deleteAudioBackground(id: string) {
      return graphql.query<{
        audioBackgroundDelete: { _id: string }
      }>(`mutation {
        audioBackgroundDelete(_id: "${id}") { _id }
      }`)
    },

    // Gongs
    async createGong(slug: string) {
      return graphql.query<{
        gongCreate: { _id: string; slug: string }
      }>(`mutation {
        gongCreate(record: { slug: "${slug}" }) { _id, slug }
      }`)
    },
    async updateGong(gong: Update<Gong>) {
      return (
        await graphql.query<{ gongUpdate: Gong }>(`mutation {
        gongUpdate(record: ${serializeRecord(gong)}) { ${gongSelection} }
      }`)
      ).gongUpdate
    },
    async uploadGong(parameters: { id: string }) {
      return graphql.query(`mutation { gongTrackUpload(
        gongId: "${parameters.id}",
        cache: ${Date.now()}
      ) }`)
    },
    async uncacheGong(parameters: { id: string }) {
      return graphql.query(`mutation { gongTrackDelete(
        gongId: "${parameters.id}"
      ) }`)
    },
    async deleteGong(id: string) {
      return graphql.query<{
        gongDelete: { _id: string }
      }>(`mutation {
        gongDelete(_id: "${id}") { _id }
      }`)
    },

    // Categories
    async createCategory(category: {
      slug: string
      short: { fr: string; en: string }
      long: { fr: string; en: string }
      type: 'theme' | 'book' | 'background'
      parent?: string
    }) {
      return graphql.query<{
        categoryCreate: {
          _id: string
          slug: string
          short: { fr: string; en: string }
          long: { fr: string; en: string }
          image: string
          type: 'theme' | 'book' | 'background'
        }
      }>(`mutation {
        categoryCreate(record: ${serializeRecord(category)}) {
          _id, slug, short, long, image, type
        }
      }`)
    },
    async updateCategory(
      category: Update<import('../state/models/audio').Category>,
    ) {
      return (
        await graphql.query<{
          categoryUpdate: import('../state/models/audio').Category
        }>(`mutation {
        categoryUpdate(record: ${serializeRecord(
          category,
        )}) { ${categorySelection} }
      }`)
      ).categoryUpdate
    },
    async deleteCategory(id: string) {
      return graphql.query<{
        categoryDelete: { _id: string }
      }>(`mutation {
        categoryDelete(_id: "${id}") { _id }
      }`)
    },

    // Users
    userQuitSpace(user: string, space: string) {
      return graphql.query(
        `mutation { userQuitSpace(user: "${user}", space: "${space}") }`,
      )
    },
    deleteUser(user: string) {
      return graphql.query(`mutation { userDelete(user: "${user}") }`)
    },

    // Comptes d'accès espace (rôle client)
    createClientAccount(email: string, space: string) {
      return graphql.query<{
        administratorCreateClientAccount: {
          _id: string | null
          email: string | null
          code: string | null
          // Vrai si l'espace a été ajouté à un compte existant : pas de nouveau
          // code, le compte conserve le sien.
          attached: boolean | null
          error: string | null
        }
      }>(`mutation {
        administratorCreateClientAccount(email: "${email}", space: "${space}") {
          _id
          email
          code
          attached
          error
        }
      }`)
    },
    regenerateClientCode(id: string) {
      return graphql.query<{
        administratorRegenerateClientCode: {
          _id: string | null
          email: string | null
          code: string | null
          error: string | null
        }
      }>(`mutation {
        administratorRegenerateClientCode(_id: "${id}") {
          _id
          email
          code
          error
        }
      }`)
    },
    // `space` : l'espace depuis lequel on retire le compte. L'API ne détache
    // que celui-ci si le compte est rattaché à plusieurs espaces.
    deleteClientAccount(id: string, space: string) {
      return graphql.query<{ administratorDeleteClientAccount: boolean }>(
        `mutation { administratorDeleteClientAccount(_id: "${id}", space: "${space}") }`,
      )
    },

    // Double authentification (#7512)
    //
    // `challenge` : connexion en cours (pas encore de session). Absent ⇒ c'est
    // la session courante qui fait foi (reconfiguration depuis « Mon compte »).
    totpEnroll(challenge?: string | null) {
      return graphql.query<{
        administratorTotpEnroll: {
          otpauthUrl: string | null
          secret: string | null
          error: string | null
        }
      }>(`mutation {
        administratorTotpEnroll${
          challenge ? `(challenge: "${challenge}")` : ''
        } {
          otpauthUrl
          secret
          error
        }
      }`)
    },
    // `code` est validé en /^[0-9]{6}$/ par l'appelant : les requêtes de ce
    // service sont interpolées sans variables GraphQL ni échappement.
    totpVerify(code: string, challenge?: string | null, remember = false) {
      return graphql.query<{
        administratorTotpVerify: {
          status: 'ok' | null
          token: string | null
          deviceToken: string | null
          error: 'invalid_code' | 'locked' | 'not_found' | 'not_allowed' | null
          lockedUntil: number | null
        }
      }>(`mutation {
        administratorTotpVerify(code: "${code}"${
        challenge ? `, challenge: "${challenge}"` : ''
      }, remember: ${remember ? 'true' : 'false'}) {
          status
          token
          deviceToken
          error
          lockedUntil
        }
      }`)
    },
    // Suppression douce : le compte disparaît de la liste et perd tout accès,
    // mais reste en base pour l'historique.
    deleteAdminAccount(id: string) {
      return graphql.query<{
        administratorDeleteAdminAccount: {
          _id: string | null
          email: string | null
          error: 'not_found' | 'cannot_delete_self' | 'last_admin' | null
        }
      }>(`mutation {
        administratorDeleteAdminAccount(_id: "${id}") {
          _id
          email
          error
        }
      }`)
    },
    totpReset(id: string) {
      return graphql.query<{ administratorTotpReset: boolean }>(
        `mutation { administratorTotpReset(_id: "${id}") }`,
      )
    },

    // Comptes administrateur nominatifs (#7512). Le compte naît sans mot de
    // passe : `invitationUrl` est la seule façon d'en définir un. Il est
    // renvoyé même quand `emailSent` est faux, pour pouvoir le transmettre à
    // la main — tous les domaines ne reçoivent pas nos emails.
    createAdminAccount(email: string) {
      return graphql.query<{
        administratorCreateAdminAccount: {
          _id: string | null
          email: string | null
          invitationUrl: string | null
          emailSent: boolean | null
          error:
            | 'invalid_email'
            | 'email_already_used'
            | 'creation_failed'
            | null
        }
      }>(`mutation {
        administratorCreateAdminAccount(email: "${email}") {
          _id
          email
          invitationUrl
          emailSent
          error
        }
      }`)
    },
    inviteAdminAccount(id: string) {
      return graphql.query<{
        administratorInviteAdminAccount: {
          _id: string | null
          email: string | null
          invitationUrl: string | null
          emailSent: boolean | null
          error: 'not_found' | 'already_active' | null
        }
      }>(`mutation {
        administratorInviteAdminAccount(_id: "${id}") {
          _id
          email
          invitationUrl
          emailSent
          error
        }
      }`)
    },
  },
}

// Loaders
const spaceStatisticsLoader = new DataLoader<
  string,
  {
    statistics: {
      users: number
      naps: number
      rooms: number
      cocoons: number
    }
  }
>(async (ids) => {
  const { spaceList } = await graphql.queries.getSpacesStatistics(ids)

  return ids.map((id) => spaceList.find((space) => space._id === id)!)
})
